import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EffectRef,
  Injector,
  Input,
  ViewChild,
  effect,
  inject,
  input,
  runInInjectionContext,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import {
  DateAdapter,
  provideNativeDateAdapter,
} from '@angular/material/core';
import {
  MatCalendar,
  MatCalendarCellClassFunction,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import {
  AgendaDataService,
  EstabelecimentoDataService,
} from '@nomoh-monorepo/data-access-bardo-administrador';
import { I18nService } from '@nomoh-monorepo/data-access-shared-i18n';
import { DateTime } from 'luxon';
import { DatePickerModule } from 'primeng/datepicker';
import { AgendaFiltroCalendarioCabecalhoComponent } from './agenda-filtro-calendario-cabecalho/agenda-filtro-calendario-cabecalho.component';

@Component({
  selector: 'lib-agenda-filtro-calendario',
  standalone: true,
  imports: [
    DatePickerModule,
    MatDatepickerModule,
    MatCardModule,
    CommonModule,
    MatIconModule,
    MatFormFieldModule,
  ],
  providers: [
    provideNativeDateAdapter(),
  ],
  templateUrl: './agenda-filtro-calendario.component.html',
  styleUrls: ['./agenda-filtro-calendario.component.scss'],
})
export class AgendaFiltroCalendarioComponent implements AfterViewInit {
  private readonly timeZonePadrao = 'America/Sao_Paulo';

  /* ----- API pública ----- */
  @Input() selected: Date | null = new Date();

  // TODO: Criar AgendaFiltroService
  // agendaFiltroService = inject(AgendaFiltroService);

  AgendaDataService = inject(AgendaDataService);
  private estabelecimentoDataService = inject(EstabelecimentoDataService);
  private dateAdapter = inject(DateAdapter<Date>);
  private i18nService = inject(I18nService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);

  datasMarcadas: Date[] = [];
  private feriadosDatas: string[] = [];

  visualizarDiasConfirmados = false;

  $mostrarConteudo = input.required<boolean>(); // <-- InputSignal
  cabecalhoComponent = AgendaFiltroCalendarioCabecalhoComponent;

  @ViewChild(MatCalendar) calendar!: MatCalendar<Date>;

  private effectsRegistrados = false;
  private visualizarEffect?: EffectRef;
  private agendaEffect?: EffectRef;
  private feriadosEffect?: EffectRef;
  private dataSelecionadaEffect?: EffectRef;
  private idiomaEffect?: EffectRef;

  ngAfterViewInit(): void {
    this.sincronizarDataSelecionada();
    this.registrarEffects();
    this.atualizarCalendario();
  }

  onSelected(novaData: Date) {
    this.selected = novaData;
    // TODO: Descomentar quando AgendaFiltroService estiver disponível
    // this.agendaFiltroService.alterarData(novaData);
    this.AgendaDataService.setDataSelecionada(novaData);
  }

  private registrarEffects() {
    if (this.effectsRegistrados) {
      return;
    }

    runInInjectionContext(this.injector, () => {
      this.dataSelecionadaEffect = effect(() => {
        this.sincronizarDataSelecionada();
      });

      this.visualizarEffect = effect(
        () => {
          this.visualizarDiasConfirmados =
            this.AgendaDataService.$visualizarDiasConfirmados();
          this.atualizarCalendario();
        }
      );

      this.agendaEffect = effect(
        () => {
          const agenda = this.AgendaDataService.$agendaInDb();
          if (agenda.agendamentos != null && agenda.agendamentos != undefined) {
            this.datasMarcadas = agenda.agendamentos
              ?.map((f) => this.toCalendarDate(f.dataHora))
              .filter((data): data is Date => !!data);

            this.atualizarCalendario();
          } else {
            this.datasMarcadas = [];
            this.atualizarCalendario();
          }
        }
      );

      this.feriadosEffect = effect(
        () => {
          const feriados = this.AgendaDataService.$feriados();
          this.feriadosDatas = feriados
            .map((feriado) => {
              if (!feriado?.date) {
                return null;
              }

              const parsed = DateTime.fromISO(feriado.date, { zone: 'utc' });
              return parsed.isValid ? parsed.toISODate() : null;
            })
            .filter((value): value is string => Boolean(value));

          this.atualizarCalendario();
        }
      );

      this.idiomaEffect = effect(() => {
        const locale = this.i18nService.$locale();
        this.dateAdapter.setLocale(locale);
        if (this.calendar) {
          this.calendar.updateTodaysDate();
          this.calendar.stateChanges.next();
          this.cdr.markForCheck();
        }
      });
    });

    this.destroyRef.onDestroy(() => {
      this.visualizarEffect?.destroy();
      this.agendaEffect?.destroy();
      this.feriadosEffect?.destroy();
      this.dataSelecionadaEffect?.destroy();
      this.idiomaEffect?.destroy();
    });

    this.effectsRegistrados = true;
  }

  private sincronizarDataSelecionada(): void {
    const dataSelecionada = this.AgendaDataService.$dataSelecionada();
    this.selected = dataSelecionada;

    if (!this.calendar) {
      return;
    }

    this.calendar.activeDate = dataSelecionada;
    this.calendar.updateTodaysDate();
    this.calendar.stateChanges.next();
    this.cdr.markForCheck();
  }

  private atualizarCalendario() {
    queueMicrotask(() => {
      if (!this.calendar) {
        return;
      }

      const calendar = this.calendar;
      calendar.updateTodaysDate();
      calendar.stateChanges.next();

      // Garante que o calendário sempre volte para a visualização mensal
      calendar.currentView = 'month';
      this.cdr.detectChanges();
    });
  }

  dateClass: MatCalendarCellClassFunction<Date> = (cellDate, view) => {
    if (view !== 'month') {
      return [];
    }

    const classes: string[] = [];

    if (this.visualizarDiasConfirmados) {
      const temAgendamento = this.datasMarcadas.some((dataMarcada) =>
        this.comparaSomenteData(dataMarcada, cellDate)
      );

      if (temAgendamento) {
        classes.push('n-possui-agendamento');
      }
    }

    if (this.diaEhFeriado(cellDate)) {
      classes.push('n-dia-feriado');
    }

    return classes;
  };

  private diaEhFeriado(cellDate: Date): boolean {
    const dataISO = this.toCalendarDateTime(cellDate).toISODate();
    if (!dataISO) {
      return false;
    }

    return this.feriadosDatas.includes(dataISO);
  }

  private comparaSomenteData(a: Date, b: Date): boolean {
    return this.toCalendarDateTime(a).hasSame(this.toCalendarDateTime(b), 'day');
  }

  private toCalendarDate(
    valor: Date | string | null | undefined
  ): Date | null {
    if (!valor) {
      return null;
    }

    const dateTime =
      valor instanceof Date
        ? DateTime.fromJSDate(valor)
        : DateTime.fromISO(valor);

    if (!dateTime.isValid) {
      return null;
    }

    const local = dateTime.setZone(this.getTimeZoneEstabelecimento());
    return new Date(local.year, local.month - 1, local.day, 12);
  }

  private toCalendarDateTime(data: Date): DateTime {
    return DateTime.fromObject(
      {
        year: data.getFullYear(),
        month: data.getMonth() + 1,
        day: data.getDate(),
      },
      { zone: this.getTimeZoneEstabelecimento() }
    );
  }

  private getTimeZoneEstabelecimento(): string {
    const timeZone =
      this.estabelecimentoDataService
        .$estabelecimentoAtual()
        ?.timeZone?.trim() || this.timeZonePadrao;
    return DateTime.now().setZone(timeZone).isValid
      ? timeZone
      : this.timeZonePadrao;
  }
}
